import { BaseLayout } from '@/components/layout/BaseLayout'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState } from '@/components/common/EmptyState'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { useStore } from '@/features/stores/StoreContext'
import { supabase } from '@/lib/supabase/client'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Plus, GripVertical, Eye, EyeOff, Pencil, Trash2, Layout } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

interface Section {
  id: string
  type: string
  title: string | null
  subtitle: string | null
  content: any
  position: number
  is_visible: boolean
}

export function HomepagePage() {
  const { currentStore } = useStore()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [editingSection, setEditingSection] = useState<Section | null>(null)
  const [formData, setFormData] = useState({
    type: 'hero',
    title: '',
    subtitle: '',
    image_url: '',
    button_text: '',
    button_link: '',
  })

  const { data: sections = [], isLoading } = useQuery({
    queryKey: ['homepage-sections', currentStore?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('homepage_sections')
        .select('*')
        .eq('store_id', currentStore?.id)
        .order('position')
      if (error) throw error
      return data as Section[]
    },
    enabled: !!currentStore?.id,
  })

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const maxPosition = sections.length > 0 ? Math.max(...sections.map(s => s.position)) : 0
      const { error } = await supabase.from('homepage_sections').insert({
        store_id: currentStore?.id,
        type: data.type,
        title: data.title || null,
        subtitle: data.subtitle || null,
        content: {
          image_url: data.image_url,
          button_text: data.button_text,
          button_link: data.button_link,
        },
        position: maxPosition + 1,
      })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['homepage-sections'] })
      toast.success('Section added')
      handleClose()
    },
    onError: () => toast.error('Failed to add section'),
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof formData }) => {
      const { error } = await supabase.from('homepage_sections').update({
        type: data.type,
        title: data.title || null,
        subtitle: data.subtitle || null,
        content: {
          image_url: data.image_url,
          button_text: data.button_text,
          button_link: data.button_link,
        },
      }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['homepage-sections'] })
      toast.success('Section updated')
      handleClose()
    },
    onError: () => toast.error('Failed to update section'),
  })

  const toggleMutation = useMutation({
    mutationFn: async ({ id, is_visible }: { id: string; is_visible: boolean }) => {
      const { error } = await supabase.from('homepage_sections').update({ is_visible }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['homepage-sections'] })
      toast.success('Visibility updated')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('homepage_sections').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['homepage-sections'] })
      toast.success('Section deleted')
    },
    onError: () => toast.error('Failed to delete section'),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (editingSection) {
      updateMutation.mutate({ id: editingSection.id, data: formData })
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleEdit = (section: Section) => {
    setEditingSection(section)
    setFormData({
      type: section.type,
      title: section.title || '',
      subtitle: section.subtitle || '',
      image_url: section.content?.image_url || '',
      button_text: section.content?.button_text || '',
      button_link: section.content?.button_link || '',
    })
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setEditingSection(null)
    setFormData({ type: 'hero', title: '', subtitle: '', image_url: '', button_text: '', button_link: '' })
  }

  return (
    <BaseLayout>
      <div className="p-8">
        <PageHeader
          title="Homepage Builder"
          description="Design your storefront homepage"
          action={
            <Button onClick={() => setOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Section
            </Button>
          }
        />

        {isLoading ? (
          <LoadingSpinner />
        ) : sections.length === 0 ? (
          <EmptyState
            icon={Layout}
            title="No sections yet"
            description="Start building your homepage by adding sections"
            action={{ label: 'Add Section', onClick: () => setOpen(true) }}
          />
        ) : (
          <div className="space-y-4">
            {sections.map((section) => (
              <div key={section.id} className="bg-white rounded-lg border p-4">
                <div className="flex items-center gap-4">
                  <GripVertical className="h-5 w-5 text-muted-foreground cursor-move" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded font-medium">
                        {section.type.toUpperCase()}
                      </span>
                      <h3 className="font-semibold">{section.title || 'Untitled Section'}</h3>
                    </div>
                    {section.subtitle && (
                      <p className="text-sm text-muted-foreground mt-1">{section.subtitle}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => toggleMutation.mutate({ id: section.id, is_visible: !section.is_visible })}
                    >
                      {section.is_visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleEdit(section)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => deleteMutation.mutate(section.id)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingSection ? 'Edit Section' : 'Add Section'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Section Type *</Label>
                <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hero">🎯 Hero Banner</SelectItem>
                    <SelectItem value="featured_products">⭐ Featured Products</SelectItem>
                    <SelectItem value="categories">📁 Categories Grid</SelectItem>
                    <SelectItem value="banner">🎨 Promotional Banner</SelectItem>
                    <SelectItem value="text">📝 Text Block</SelectItem>
                    <SelectItem value="testimonials">💬 Customer Testimonials</SelectItem>
                    <SelectItem value="video">🎥 Video Section</SelectItem>
                    <SelectItem value="countdown_timer">⏰ Countdown Timer</SelectItem>
                    <SelectItem value="stats">📊 Statistics</SelectItem>
                    <SelectItem value="instagram_feed">📸 Instagram Feed</SelectItem>
                    <SelectItem value="newsletter">📧 Newsletter Signup</SelectItem>
                    <SelectItem value="brand_logos">🏢 Brand Logos</SelectItem>
                    <SelectItem value="trust_badges">✅ Trust Badges</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Section title"
                />
              </div>
              <div>
                <Label>Subtitle</Label>
                <Textarea
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  placeholder="Section subtitle or description"
                  rows={2}
                />
              </div>
              {(formData.type === 'hero' || formData.type === 'banner') && (
                <>
                  <div>
                    <Label>Image URL</Label>
                    <Input
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      placeholder="https://..."
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Button Text</Label>
                      <Input
                        value={formData.button_text}
                        onChange={(e) => setFormData({ ...formData, button_text: e.target.value })}
                        placeholder="Shop Now"
                      />
                    </div>
                    <div>
                      <Label>Button Link</Label>
                      <Input
                        value={formData.button_link}
                        onChange={(e) => setFormData({ ...formData, button_link: e.target.value })}
                        placeholder="/products"
                      />
                    </div>
                  </div>
                </>
              )}
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                  {editingSection ? 'Update' : 'Add'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </BaseLayout>
  )
}
